import { createFileRoute } from "@tanstack/react-router";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { PageHero } from "@/components/page-hero";
import { Section, SectionTitle } from "@/components/section";
import { Reveal } from "@/components/reveal";
import { ResponsiveImg } from "@/components/responsive-img";
import { BUSINESS, PROCESS } from "@/lib/site-data";
import { lcpPreload, imgSizes } from "@/lib/images";
import { ContactForm } from "@/components/contact-form";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact | Call Shirley at Magnolia House, Fuquay-Varina" },
      {
        name: "description",
        content:
          "Call (919) 552-6802 to book a custom framing design appointment with Shirley Hunsberger, CPF, at Magnolia House in Fuquay-Varina, NC.",
      },
      {
        property: "og:title",
        content: "Contact Magnolia House Custom Picture Framing",
      },
      {
        property: "og:description",
        content: "By-appointment framing consultations, most days and times including after hours.",
      },
    ],
    links: [lcpPreload("frame-selection")],
  }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <>
      <PageHero
        eyebrow="Contact"
        title={
          <>
            Call Shirley for
            <br />
            <span className="amber-text italic">an appointment.</span>
          </>
        }
        lead="One phone call books an unhurried design consultation — bring your piece and we'll work it out together."
        image="frame-selection"
        alt="Frame corner samples"
      />

      <Section>
        <div className="grid gap-14 lg:grid-cols-2">
          <div>
            <a
              href={BUSINESS.phoneHref}
              className="display block text-5xl text-primary transition-opacity duration-500 hover:opacity-70 sm:text-6xl"
            >
              {BUSINESS.phone}
            </a>
            <ul className="mt-10 space-y-6 text-sm">
              <li className="flex gap-4">
                <Mail className="mt-0.5 size-4 shrink-0 text-[var(--terracotta)]" aria-hidden />
                <a href={`mailto:${BUSINESS.email}`} className="link-underline">
                  {BUSINESS.email}
                </a>
              </li>
              <li className="flex gap-4">
                <MapPin className="mt-0.5 size-4 shrink-0 text-[var(--terracotta)]" aria-hidden />
                <a
                  href={BUSINESS.mapsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline"
                >
                  {BUSINESS.address}
                  <span className="sr-only"> (opens in Google Maps)</span>
                </a>
              </li>
              <li className="flex gap-4">
                <Clock className="mt-0.5 size-4 shrink-0 text-[var(--terracotta)]" aria-hidden />
                <span className="text-muted-foreground">
                  By appointment — most days and times, including after hours.
                </span>
              </li>
            </ul>
            <a href={BUSINESS.phoneHref} className="btn-primary mt-10">
              <Phone className="size-3.5" />
              Call Shirley now
            </a>
          </div>

          <Reveal delay={100}>
            <div className="plate">
              <ResponsiveImg
                name="shirley-photo"
                sizes={imgSizes.portrait}
                alt="Shirley Hunsberger, Certified Professional Framer"
                loading="lazy"
                decoding="async"
                className="aspect-[4/5] w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </Section>

      <Section>
        <SectionTitle eyebrow="Or send a note" title="Tell Shirley about your piece." />
        <div className="mt-12 max-w-2xl">
          <ContactForm />
        </div>
      </Section>

      <Section tone="night">
        <SectionTitle light eyebrow="What happens next" title="After you call." />
        <ol className="mt-14 grid gap-8 md:grid-cols-3 lg:grid-cols-5">
          {PROCESS.map((p, i) => (
            <Reveal as="li" key={p.step} delay={i * 80}>
              <p className="display text-5xl text-[var(--amber-warm)]">{p.step}</p>
              <h3 className="mt-4 text-sm font-semibold uppercase tracking-[0.16em]">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
            </Reveal>
          ))}
        </ol>
      </Section>
    </>
  );
}
